<template>
  <div class="dd-page">
    <header class="dd-header">
      <strong>Docker配信システム</strong
      ><button @click="logout">ログアウト</button>
    </header>
    <main class="dd-main">
      <div class="dd-title">
        <div>
          <h1>ユーザ管理</h1>
          <p>契約会社から提出された利用ユーザ情報と取得許可を管理します。</p>
        </div>
        <button class="secondary" @click="$router.push('/menu')">
          メニューへ戻る
        </button>
      </div>
      <p v-if="error" role="alert" class="dd-message error">{{ error }}</p>
      <p v-if="message" role="status" class="dd-message">{{ message }}</p>
      <section class="dd-panel">
        <div class="dd-tabs">
          <button
            v-for="(label, key) in tabs"
            :key="key"
            :class="{ active: tab === key }"
            @click="switchTab(key)"
          >
            {{ label }}
          </button>
        </div>
        <div v-if="tab === 'users'">
          <div class="dd-toolbar">
            <input
              v-model="search"
              placeholder="会社名・利用者名・ログインIDで検索"
              aria-label="ユーザ検索"
              style="flex: 1"
            /><select v-model="typeFilter" aria-label="ユーザ区分">
              <option value="">すべての区分</option>
              <option v-for="(label, key) in userTypes" :key="key" :value="key">
                {{ label }}
              </option></select
            ><button :disabled="busy || !loaded" @click="editUser()">
              新規ユーザ登録
            </button>
          </div>
          <div class="dd-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>会社名 / 所属</th>
                  <th>利用者名 / ログインID</th>
                  <th>区分</th>
                  <th>状態</th>
                  <th>利用終了</th>
                  <th>許可</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in filtered" :key="u.user_id">
                  <td>
                    {{ companyName(u.company_id)
                    }}<small>{{ u.department }}</small>
                  </td>
                  <td>
                    {{ u.display_name
                    }}<small>{{ u.username }} / ID {{ u.user_id }}</small>
                  </td>
                  <td>{{ userTypes[u.user_type] }}</td>
                  <td>{{ u.is_active ? '有効' : '無効' }}</td>
                  <td>{{ prettyDate(u.valid_until, true) }}</td>
                  <td>{{ u.grants.length }}件</td>
                  <td>
                    <button
                      class="secondary"
                      :disabled="busy"
                      @click="editUser(u)"
                    >
                      編集
                    </button>
                  </td>
                </tr>
                <tr v-if="!filtered.length">
                  <td colspan="7" class="dd-empty">
                    {{ loaded ? '該当するユーザはありません' : '読み込み中' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else-if="tab === 'companies'">
          <div class="dd-toolbar">
            <p class="dd-note" style="flex: 1">
              会社側責任者は連絡窓口です。配信管理者権限とは別に管理します。
            </p>
            <button :disabled="busy || !loaded" @click="editCompany()">
              会社登録
            </button>
          </div>
          <div class="dd-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>会社名 / ID</th>
                  <th>管理責任者 / 所属</th>
                  <th>連絡先</th>
                  <th>状態</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in companies" :key="c.company_id">
                  <td>
                    {{ c.company_name }}<small>ID {{ c.company_id }}</small>
                  </td>
                  <td>
                    {{ c.manager_name
                    }}<small>{{ c.manager_department }}</small>
                  </td>
                  <td>
                    {{ c.contact_email }}<small>{{ c.contact_phone }}</small>
                  </td>
                  <td>{{ c.is_active ? '有効' : '無効' }}</td>
                  <td>
                    <button
                      class="secondary"
                      :disabled="busy"
                      @click="editCompany(c)"
                    >
                      編集
                    </button>
                  </td>
                </tr>
                <tr v-if="!companies.length">
                  <td colspan="5" class="dd-empty">会社を登録してください</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else-if="tab === 'contracts'">
          <div class="dd-toolbar">
            <button
              :disabled="busy || !loaded || !companies.length"
              @click="editContract()"
            >
              契約登録
            </button>
          </div>
          <div class="dd-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>会社 / 契約番号</th>
                  <th>契約区分</th>
                  <th>状態</th>
                  <th>契約終了</th>
                  <th>対象</th>
                  <th>上限</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in contracts" :key="c.contract_id">
                  <td>
                    {{ companyName(c.company_id)
                    }}<small>{{ c.contract_no }}</small>
                  </td>
                  <td>{{ contractTypes[c.contract_type] }}</td>
                  <td>{{ contractStates[c.contract_status] }}</td>
                  <td>{{ prettyDate(c.valid_until, true) }}</td>
                  <td>
                    {{ c.allowed_configs.join(' / ')
                    }}<small>{{ c.allowed_kinds.join(' / ') }}</small>
                  </td>
                  <td>{{ c.user_limit || '未設定' }}</td>
                  <td>
                    <button
                      class="secondary"
                      :disabled="busy"
                      @click="editContract(c)"
                    >
                      編集
                    </button>
                  </td>
                </tr>
                <tr v-if="!contracts.length">
                  <td colspan="7" class="dd-empty">契約を登録してください</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else>
          <button class="secondary" :disabled="busy" @click="loadAudit">
            履歴を再読込
          </button>
          <div class="dd-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>日時</th>
                  <th>操作者</th>
                  <th>対象</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="a in audits" :key="a.audit_id">
                  <td>{{ prettyTime(a.created_at) }}</td>
                  <td>{{ a.actor }}</td>
                  <td>{{ a.entity_type }} / {{ a.entity_id }}</td>
                  <td>{{ a.action }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <section v-if="kind" ref="editor" class="dd-panel">
        <h2>{{ formId ? '情報の編集' : '新規登録' }} — {{ tabs[kind] }}</h2>
        <form @submit.prevent="save">
          <fieldset
            :disabled="busy"
            style="border: 0; padding: 0; margin: 0; min-width: 0"
          >
            <div v-if="kind === 'companies'" class="dd-grid">
              <label
                >会社名（必須）<input
                  v-model="form.company_name"
                  required
                  maxlength="200" /></label
              ><label
                >会社側管理責任者（必須）<input
                  v-model="form.manager_name"
                  required
                  maxlength="100" /></label
              ><label
                >管理責任者の所属<input
                  v-model="form.manager_department"
                  maxlength="100" /></label
              ><label
                >会社連絡先メール（必須）<input
                  v-model="form.contact_email"
                  type="email"
                  required
                  maxlength="254" /></label
              ><label
                >会社連絡先電話<input
                  v-model="form.contact_phone"
                  maxlength="40" /></label
              ><label
                >有効状態<select v-model="form.is_active">
                  <option :value="true">有効</option>
                  <option :value="false">無効</option>
                </select></label
              >
            </div>
            <div v-else-if="kind === 'contracts'" class="dd-grid">
              <label
                >会社（必須）<select
                  v-model.number="form.company_id"
                  required
                  :disabled="!!formId"
                >
                  <option
                    v-for="c in companies"
                    :key="c.company_id"
                    :value="c.company_id"
                  >
                    {{ c.company_name }}
                  </option>
                </select></label
              ><label
                >契約番号（必須）<input
                  v-model="form.contract_no"
                  required
                  maxlength="100" /></label
              ><label
                >契約区分<select v-model="form.contract_type">
                  <option
                    v-for="(label, key) in contractTypes"
                    :key="key"
                    :value="key"
                  >
                    {{ label }}
                  </option>
                </select></label
              ><label
                >契約状態<select v-model="form.contract_status">
                  <option
                    v-for="(label, key) in contractStates"
                    :key="key"
                    :value="key"
                  >
                    {{ label }}
                  </option>
                </select></label
              ><label
                >契約開始（日本時間）<input
                  v-model="form.valid_from"
                  type="date" /></label
              ><label
                >契約終了（日本時間）<input
                  v-model="form.valid_until"
                  type="date"
                  :required="form.contract_type === 'poc_trial'"
                /><small>終了日の翌日0時から利用不可</small></label
              ><label
                >有効登録人数上限<input
                  v-model="form.user_limit"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="未設定は上限未設定"
              /></label>
              <div>
                <label>対象構成</label
                ><label v-for="v in ['SA', 'CS']" :key="v" class="dd-check"
                  ><input
                    v-model="form.allowed_configs"
                    type="checkbox"
                    :value="v"
                  />{{ v }}</label
                >
              </div>
              <div>
                <label>配布区分</label
                ><label class="dd-check"
                  ><input
                    v-model="form.allowed_kinds"
                    type="checkbox"
                    value="IN"
                  />初回導入</label
                ><label class="dd-check"
                  ><input
                    v-model="form.allowed_kinds"
                    type="checkbox"
                    value="UP"
                  />更新</label
                >
              </div>
            </div>
            <template v-else
              ><div class="dd-grid">
                <label
                  >会社（必須）<select
                    v-model.number="form.company_id"
                    required
                    @change="changeCompany"
                  >
                    <option
                      v-for="c in companies"
                      :key="c.company_id"
                      :value="c.company_id"
                    >
                      {{ c.company_name }}
                    </option>
                  </select></label
                ><label
                  >適用契約（必須）<select
                    v-model.number="form.contract_id"
                    required
                    @change="form.grants = []"
                  >
                    <option
                      v-for="c in companyContracts"
                      :key="c.contract_id"
                      :value="c.contract_id"
                    >
                      {{ c.contract_no }} / {{ contractTypes[c.contract_type] }}
                    </option>
                  </select></label
                ><label
                  >登録ユーザ名（必須）<input
                    v-model="form.display_name"
                    required
                    maxlength="100" /></label
                ><label
                  >所属部署<input
                    v-model="form.department"
                    maxlength="100" /></label
                ><label
                  >ログインID（必須）<input
                    v-model="form.username"
                    required
                    minlength="3"
                    maxlength="100"
                    autocomplete="off" /></label
                ><label
                  >初期パスワード / 再設定<input
                    v-model="form.password"
                    type="password"
                    :required="!formId"
                    minlength="10"
                    autocomplete="new-password"
                  /><small
                    >10文字以上・UTF-8で72バイト以下。編集時の空欄は変更なし</small
                  ></label
                ><label
                  >利用者メール<input
                    v-model="form.email"
                    type="email"
                    maxlength="254" /></label
                ><label
                  >利用者電話<input
                    v-model="form.phone"
                    maxlength="40" /></label
                ><label
                  >ユーザ区分<select v-model="form.user_type">
                    <option
                      v-for="(label, key) in userTypes"
                      :key="key"
                      :value="key"
                    >
                      {{ label }}
                    </option>
                  </select></label
                ><label
                  >有効状態<select v-model="form.is_active">
                    <option :value="true">有効</option>
                    <option :value="false">無効</option>
                  </select></label
                ><label
                  >利用開始（日本時間）<input
                    v-model="form.valid_from"
                    type="date" /></label
                ><label
                  >利用終了（日本時間）<input
                    v-model="form.valid_until"
                    type="date"
                  /><small>終了日の翌日0時から利用不可</small></label
                ><label class="wide"
                  >備考<textarea
                    v-model="form.notes"
                    maxlength="1000"
                    rows="2"
                  />
                </label>
              </div>
              <div class="dd-section">
                <h3>取得を許可するInstaller</h3>
                <p class="dd-note">
                  適用契約の構成・配布区分に合う版を表示します。テスト公開前に許可を設定できます。
                </p>
                <div class="dd-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>許可</th>
                        <th>Version / ファイル名</th>
                        <th>公開状態</th>
                        <th>許可終了日</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="i in grantCandidates" :key="i.docker_id">
                        <td>
                          <input
                            type="checkbox"
                            :checked="hasGrant(i.docker_id)"
                            :aria-label="i.docker_version + 'の取得許可'"
                            @change="
                              toggleGrant(i.docker_id, $event.target.checked)
                            "
                          />
                        </td>
                        <td>
                          {{ i.docker_version }}<small>{{ i.file_name }}</small>
                        </td>
                        <td>{{ statuses[i.publication_status] }}</td>
                        <td>
                          <input
                            v-if="hasGrant(i.docker_id)"
                            v-model="
                              form.grants.find(
                                (g) => g.docker_id === i.docker_id
                              ).valid_until
                            "
                            type="date"
                            aria-label="許可終了日"
                          />
                        </td>
                      </tr>
                      <tr v-if="!grantCandidates.length">
                        <td colspan="4" class="dd-empty">
                          対象契約に合うInstallerがありません。先にInstallerの構成・配布区分を設定してください。
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div></template
            >
            <div class="dd-actions">
              <button type="submit">{{ busy ? '保存中…' : '保存' }}</button
              ><button type="button" class="secondary" @click="close">
                キャンセル
              </button>
            </div>
          </fieldset>
        </form>
      </section>
    </main>
  </div>
</template>
<script>
import api from '../api';
import {
  userTypes,
  contractTypes,
  statuses,
  dateInput,
  dateUtc,
  prettyDate,
  prettyTime,
} from '../fields';
export default {
  name: 'UserManageView',
  data() {
    return {
      tabs: {
        users: '利用ユーザ',
        companies: '会社情報',
        contracts: '契約情報',
        audit: '操作履歴',
      },
      tab: 'users',
      userTypes,
      contractTypes,
      statuses,
      contractStates: { active: '有効', stopped: '停止', ended: '終了' },
      users: [],
      companies: [],
      contracts: [],
      installers: [],
      audits: [],
      search: '',
      typeFilter: '',
      kind: '',
      form: {},
      formId: null,
      loaded: false,
      busy: false,
      error: '',
      message: '',
    };
  },
  computed: {
    filtered() {
      const q = this.search.trim().toLowerCase();
      return this.users.filter(
        (u) =>
          (!this.typeFilter || u.user_type === this.typeFilter) &&
          (!q ||
            [u.display_name, u.username, this.companyName(u.company_id)].some(
              (s) => s.toLowerCase().includes(q)
            ))
      );
    },
    companyContracts() {
      return this.contracts.filter(
        (c) => c.company_id === this.form.company_id
      );
    },
    grantCandidates() {
      const c = this.contracts.find(
        (c) => c.contract_id === this.form.contract_id
      );
      return c
        ? this.installers.filter(
            (i) =>
              c.allowed_configs.includes(i.configuration) &&
              c.allowed_kinds.includes(i.install_kind)
          )
        : [];
    },
  },
  created() {
    this.load();
  },
  beforeUnmount() {
    this.form.password = '';
  },
  methods: {
    prettyDate,
    prettyTime,
    companyName(id) {
      return (
        this.companies.find((c) => c.company_id === id)?.company_name ||
        String(id)
      );
    },
    async load() {
      this.busy = true;
      this.error = '';
      try {
        const responses = await Promise.all([
          api.get('/api/users'),
          api.get('/api/admin/companies'),
          api.get('/api/admin/contracts'),
          api.get('/api/users/installers'),
        ]);
        [this.users, this.companies, this.contracts, this.installers] =
          responses.map((r) => r.data);
        this.loaded = true;
      } catch (e) {
        this.error = e.response?.data?.error || '読み込みに失敗しました';
      } finally {
        this.busy = false;
      }
    },
    async loadAudit() {
      try {
        this.audits = (await api.get('/api/admin/audit')).data;
      } catch (e) {
        this.error = e.response?.data?.error || '履歴取得に失敗しました';
      }
    },
    switchTab(tab) {
      if (this.kind && !confirm('編集中の内容を破棄しますか？')) return;
      this.close();
      this.tab = tab;
      if (tab === 'audit') this.loadAudit();
    },
    close() {
      this.form.password = '';
      this.form = {};
      this.formId = null;
      this.kind = '';
    },
    open(kind, row, id) {
      if (this.kind && !confirm('編集中の内容を破棄しますか？')) return false;
      this.error = '';
      this.message = '';
      this.form = JSON.parse(JSON.stringify(row));
      this.formId = id || null;
      this.kind = kind;
      this.$nextTick(() =>
        this.$refs.editor?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      );
      return true;
    },
    editCompany(c) {
      this.open(
        'companies',
        c || {
          company_name: '',
          manager_name: '',
          manager_department: '',
          contact_email: '',
          contact_phone: '',
          is_active: true,
        },
        c?.company_id
      );
    },
    editContract(c) {
      const row = c
        ? {
            ...c,
            valid_from: dateInput(c.valid_from),
            valid_until: dateInput(c.valid_until, true),
          }
        : {
            company_id: this.companies[0]?.company_id,
            contract_no: '',
            contract_type: 'licensed',
            contract_status: 'active',
            valid_from: '',
            valid_until: '',
            user_limit: null,
            allowed_configs: ['SA'],
            allowed_kinds: ['IN', 'UP'],
          };
      this.open('contracts', row, c?.contract_id);
    },
    editUser(u) {
      if (!this.companies.length || !this.contracts.length) {
        this.error = '先に会社と契約を登録してください';
        return;
      }
      const c = this.contracts[0];
      const row = u
        ? {
            ...u,
            password: '',
            valid_from: dateInput(u.valid_from),
            valid_until: dateInput(u.valid_until, true),
            grants: u.grants.map((g) => ({
              ...g,
              valid_until: dateInput(g.valid_until, true),
            })),
          }
        : {
            company_id: c.company_id,
            contract_id: c.contract_id,
            display_name: '',
            department: '',
            email: '',
            phone: '',
            username: '',
            password: '',
            user_type:
              c.contract_type === 'poc_trial'
                ? 'poc_trial'
                : c.contract_type === 'internal'
                ? 'developer_tester'
                : 'licensed_user',
            is_active: true,
            valid_from: '',
            valid_until: '',
            notes: '',
            grants: [],
          };
      this.open('users', row, u?.user_id);
    },
    changeCompany() {
      this.form.contract_id = this.companyContracts[0]?.contract_id || null;
      this.form.grants = [];
    },
    hasGrant(id) {
      return this.form.grants.some((g) => g.docker_id === id);
    },
    toggleGrant(id, checked) {
      if (checked) this.form.grants.push({ docker_id: id, valid_until: '' });
      else
        this.form.grants = this.form.grants.filter((g) => g.docker_id !== id);
    },
    async save() {
      if (this.busy) return;
      this.error = '';
      this.message = '';
      try {
        const body = { ...this.form };
        if (this.kind !== 'companies') {
          body.valid_from = dateUtc(body.valid_from);
          body.valid_until = dateUtc(body.valid_until, true);
        }
        if (this.kind === 'contracts')
          body.user_limit =
            body.user_limit === '' || body.user_limit == null
              ? null
              : Number(body.user_limit);
        if (this.kind === 'users')
          body.grants = body.grants.map((g) => ({
            ...g,
            valid_until: dateUtc(g.valid_until, true),
          }));
        const route =
          this.kind === 'users' ? '/api/users' : '/api/admin/' + this.kind;
        this.busy = true;
        await api[this.formId ? 'put' : 'post'](
          route + (this.formId ? '/' + this.formId : ''),
          body
        );
        this.close();
        await this.load();
        this.message = '保存しました';
      } catch (e) {
        this.error = e.response?.data?.error || '保存に失敗しました';
      } finally {
        this.busy = false;
      }
    },
    async logout() {
      this.close();
      await api.post('/api/admin/logout');
      this.$router.push('/login');
    },
  },
};
</script>
